import { supabase } from '@/core/database/supabase';
import { permissionEngine } from '@/core/permissions';
import type { RequestRecord } from '../types/Requests.types';

function unique(values: Array<string | null | undefined>) {
  return Array.from(new Set(values.filter(Boolean))) as string[];
}

function statusLabel(status?: string | null) {
  switch (status) {
    case 'approved': return 'Đã duyệt';
    case 'rejected': return 'Đã từ chối';
    case 'issued': return 'Đã xuất';
    case 'completed': return 'Hoàn tất';
    case 'requested': return 'Chờ duyệt';
    case 'pending': return 'Đang xem xét';
    default: return 'Chờ duyệt';
  }
}

function typeLabel(type?: string | null) {
  switch (type) {
    case 'construction': return 'Thi công';
    case 'maintenance': return 'Bảo dưỡng';
    case 'material': return 'Vật tư';
    case 'equipment': return 'Thiết bị';
    default: return type || 'Đề xuất';
  }
}

export const RequestsListService = {
  async getRequests(companyId: string, userId: string): Promise<any[]> {
    await permissionEngine.assert(userId, 'inventory.requests.view', { companyId });

    const { data, error } = await supabase
      .from('requests')
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    const rows = data || [];
    const projectIds = unique(rows.map((item) => item.project_id));
    const zoneIds = unique(rows.map((item) => item.zone_id));
    const requesterIds = unique(rows.map((item) => item.requested_by));

    const [{ data: projects }, { data: zones }, { data: requesters }] = await Promise.all([
      projectIds.length
        ? supabase.from('projects').select('id, name').in('id', projectIds)
        : Promise.resolve({ data: [] }),
      zoneIds.length
        ? supabase.from('project_zones').select('id, zone_name').in('id', zoneIds)
        : Promise.resolve({ data: [] }),
      requesterIds.length
        ? supabase.from('user_profiles').select('id, full_name, avatar_path').in('id', requesterIds)
        : Promise.resolve({ data: [] }),
    ]);

    const projectMap = new Map((projects || []).map((project) => [project.id, project]));
    const zoneMap = new Map((zones || []).map((zone) => [zone.id, zone]));
    const requesterMap = new Map((requesters || []).map((requester) => [requester.id, {
      ...requester,
      avatar_url: requester.avatar_path,
    }]));

    return (data || []).map(item => ({
      id: item.id,
      companyId: item.company_id,
      projectId: item.project_id,
      zoneId: item.zone_id,
      requesterId: item.requested_by,
      code: item.request_code,
      title: item.title,
      type: typeLabel(item.request_type),
      description: item.description,
      deadlineDate: item.deadline_date,
      statusCode: item.status_code,
      status: statusLabel(item.status_code),
      priority: item.priority_code,
      relatedUserId: item.related_user_id,
      createdAt: item.created_at,
      updatedAt: item.updated_at,
      project: projectMap.get(item.project_id) || null,
      zone: zoneMap.get(item.zone_id) || null,
      requester: requesterMap.get(item.requested_by) || null,
    }));
  }
};
